import classNames from 'classnames';
import type { TFunction } from 'i18next';
import { Observer } from 'mobx-react-lite';
import numeral from 'numeral';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { RowRenderProps } from 'react-table';

import type { Conversion, IGoal } from '@feathr/blackbox';
import type { IColumn } from '@feathr/components';
import { AvatarV2 as Avatar, Pill, TableColumnHeader, Text } from '@feathr/components';
import { moment } from '@feathr/hooks';
import { useStore } from '@feathr/report_components/state';

import PersonName from '../ConversionTableCardV1/PersonName';

import * as tableStyles from '@feathr/components/dist/Table/Table.css';
import * as styles from '../ConversionTableCardV1/ConversionTableColumns.css';

interface IPersonCellProps extends RowRenderProps {
  localUrl?: (url: string) => string;
  original: Conversion;
  t: TFunction;
}

function PersonCell({ localUrl, original, t }: Readonly<IPersonCellProps>): JSX.Element {
  return (
    <Observer>
      {function useAnonymousFunction(): JSX.Element {
        const { Persons } = useStore();
        if (original.get('person')) {
          const model = Persons.get(original.get('person'));
          if (model && !model.isPending) {
            const person = model.toJS();
            return (
              <div className={styles.container}>
                <Avatar name={person.name} placeholder={person.placeholder} />
                <div className={styles.content}>
                  <div className={styles.name}>
                    <PersonName localUrl={localUrl} person={person} />{' '}
                    {model.isArchived && (
                      <Pill showPrefix={false} variant={'secondary'}>
                        {t('Archived')}
                      </Pill>
                    )}
                  </div>
                  {person.external_id && <span>{person.external_id}</span>}
                </div>
              </div>
            );
          }
        }
        return <div className={styles.container}>Anonymous</div>;
      }}
    </Observer>
  );
}

export const defaultColumnIds = [
  'd_c',
  'person',
  'goals',
  'category',
  'value',
  'window',
  'timeline',
];

export function ConversionAnalyticsColumns(
  goals: IGoal[],
  localUrl?: (url: string) => string,
): Array<IColumn<Conversion>> {
  const { t } = useTranslation();

  const columns: Array<IColumn<Conversion>> = [
    {
      Header: TableColumnHeader({ title: t('Conversion Date') }),
      id: 'd_c',
      checkboxLabel: t('Conversion Date'),
      width: 150,
      className: tableStyles.cellLeft,
      Cell({ original }): JSX.Element {
        const dateValue = original.get('d_c');
        const formattedDate = moment.utc(dateValue, moment.ISO_8601).utc().format('MMMM D, YYYY');
        return (
          <Text.DefaultHeadingMedium truncate={true}>{formattedDate}</Text.DefaultHeadingMedium>
        );
      },
    },
    {
      id: 'person',
      Header: TableColumnHeader({ title: t('Name') }),
      checkboxLabel: t('Name'),
      sortable: false,
      className: classNames(tableStyles.cellLeft, styles.person),
      Cell(row): JSX.Element {
        return <PersonCell {...row} localUrl={localUrl} t={t} />;
      },
    },
    {
      id: 'goals',
      Header: TableColumnHeader({ title: t('Goal Name') }),
      checkboxLabel: t('Goal Name'),
      sortable: false,
      className: tableStyles.cellLeft,
      Cell({ original }): JSX.Element {
        const conversionGoalIds = original.get('goals', []) as string[];
        const conversionGoals = goals.filter((g) => conversionGoalIds.includes(g.id));
        if (conversionGoals.length > 0) {
          return <>{conversionGoals.map((g) => g.name).join(', ')}</>;
        }
        return <>-</>;
      },
    },
    {
      id: 'category',
      Header: TableColumnHeader({ title: t('Category') }),
      checkboxLabel: t('Category'),
      sortable: true,
      width: 160,
      className: tableStyles.cellLeft,
      Cell({ original }): JSX.Element {
        return (
          <Text.DefaultRegular truncate={true}>{original.get('category')}</Text.DefaultRegular>
        );
      },
    },
    {
      id: 'value',
      Header: TableColumnHeader({ title: t('Value') }),
      checkboxLabel: t('Value'),
      sortable: false,
      width: 100,
      className: tableStyles.cellRight,
      Cell({ original }): JSX.Element {
        const value = original.get('computed_value');
        return (
          <Text.DefaultRegular truncate={true}>
            {numeral(value).format('$0,0.00')}
          </Text.DefaultRegular>
        );
      },
    },
    {
      Header: TableColumnHeader({ title: t('Timeframe') }),
      id: 'window',
      checkboxLabel: t('Timeframe'),
      sortable: false,
      width: 100,
      className: tableStyles.cellRight,
      Cell({ original }): JSX.Element {
        const startTS = original.get('date_enter_funnel');
        const endTS = original.get('d_c');
        const start = moment.utc(startTS, moment.ISO_8601);
        const end = moment.utc(endTS, moment.ISO_8601);
        return (
          <Text.DefaultRegular truncate={true}>
            {moment.duration(start.diff(end)).humanize()}
          </Text.DefaultRegular>
        );
      },
    },
  ];

  return columns;
}
